import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { DeprecatedRuntimeBreakpoints } from '@duda-co/runtime-breakpoints';
import { featureFlags } from '@shared-services/feature-flags';
import { logger } from '@shared-services/log-service';
import { getWindowInBrowser } from 'client/RuntimeWindow';
import { upgradeStyles } from 'client/widget-components/responsive-styles/deprecated/styled-components-utils';
import { migrateOldBPStyles } from './migrateOldBPStyles';
import {
    AnyStyleStructure,
    MigrateStylesProps,
    OldBPStyles,
    StylesType,
} from './types';

function isMigrationMonitored(monitorKey: string): boolean {
    return featureFlags.getBoolean(
        `runtime.ssr.widget.migration.${monitorKey}`,
        false
    );
}

export function normalizeStyles({
    stylesInput,
    migrationStyleKeys,
    monitorKey,
}: MigrateStylesProps) {
    if (!stylesInput) {
        return stylesInput;
    }
    try {
        const { newStyles, inputStylesType } = migrateStyles({
            stylesInput,
            migrationStyleKeys,
            monitorKey,
        });
        if (
            monitorKey &&
            inputStylesType !== StylesType.NEW_BP &&
            isMigrationMonitored(monitorKey)
        ) {
            logger.info({
                widget: monitorKey,
                key: 'breakpoints style migration',
                type: inputStylesType,
                stylesInput,
                newStyles,
                tags: ['style-migration'],
                siteAlias: getWindowInBrowser()?.Parameters?.SiteAlias,
            });
        }
        return newStyles;
    } catch (e) {
        logger.error({
            message: `Failed to migrate old styles structure`,
            errorMessage: e?.toString(),
            tags: ['style-migration', 'style-migration-error', monitorKey],
        });
    }
}

function migrateStyles({
    stylesInput = {},
    migrationStyleKeys,
    monitorKey,
}: MigrateStylesProps) {
    const currentStyleType = detectStyleStructure(stylesInput);
    let newStyles;
    switch (currentStyleType) {
        case StylesType.NO_BP:
            newStyles = migrateOldBPStyles(
                // upgradeStyles: convert no-breakpoints to old-breakpoints styles
                upgradeStyles(stylesInput as OldBPStyles) || {},
                migrationStyleKeys
            );
            break;
        case StylesType.OLD_BP:
            newStyles = migrateOldBPStyles(
                stylesInput || {},
                migrationStyleKeys
            );
            break;
        default:
            // Already on the current structure, but the section names still have to be renamed —
            // a widget that reads `styles.root` finds nothing if its stored key stays `button`.
            newStyles = renameStyleSections(stylesInput, migrationStyleKeys);
            logDroppedDeprecatedBreakpoints(newStyles, monitorKey);
            break;
    }
    return { newStyles, inputStylesType: currentStyleType };
}

function renameStyleSections(
    styles: AnyStyleStructure,
    migrationStyleKeys?: { [key: string]: string }
): AnyStyleStructure {
    if (!migrationStyleKeys || !Object.keys(migrationStyleKeys).length) {
        return styles;
    }
    return Object.entries(styles).reduce(
        (renamed: Record<string, unknown>, [styleKey, styleValue]) => {
            const targetKey = migrationStyleKeys[styleKey] || styleKey;
            const existing = renamed[targetKey];
            if (existing === undefined) {
                renamed[targetKey] = styleValue;
                return renamed;
            }
            /**
             * A stored doc can hold both the deprecated section name and the current one —
             * add-to-cart maps `button` onto `root` — and assigning would drop whichever key
             * order put first. Merge them per breakpoint instead, and let the section already
             * stored under the current name win: it is the one the widget is written against,
             * so a value it states beats the deprecated alias regardless of key order.
             */
            renamed[targetKey] =
                targetKey === styleKey
                    ? mergeRulesets(existing, styleValue)
                    : mergeRulesets(styleValue, existing);
            return renamed;
        },
        {}
    ) as AnyStyleStructure;
}

function isRuleset(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Merges two style sections a breakpoint at a time; `override`'s declarations win. */
function mergeRulesets(base: unknown, override: unknown): unknown {
    if (!isRuleset(base) || !isRuleset(override)) {
        return override ?? base;
    }
    return Object.entries(override).reduce(
        (merged: Record<string, unknown>, [breakpoint, declarations]) => {
            const baseDeclarations = merged[breakpoint];
            merged[breakpoint] =
                isRuleset(baseDeclarations) && isRuleset(declarations)
                    ? { ...baseDeclarations, ...declarations }
                    : declarations;
            return merged;
        },
        { ...base }
    );
}

const runtimeBreakpointKeys = new Set<string>(Object.values(RuntimeMediaQuery));

const deprecatedOnlyBreakpointKeys = new Set<string>(
    Object.values(DeprecatedRuntimeBreakpoints).filter(
        (key) => !runtimeBreakpointKeys.has(key)
    )
);

/**
 * A ruleset that mixes runtime breakpoints with deprecated ones is classified as already-migrated,
 * and `toCSSObject` then picks only the runtime keys — the deprecated ones disappear without a
 * trace. Nothing produces this shape today; if something starts to, this is what says so.
 *
 * Gated behind the same per-widget flag as the migration log above: this runs on every SSR render,
 * so a single stored ruleset in this shape would otherwise flood Logz.io for as long as it exists.
 */
function logDroppedDeprecatedBreakpoints(
    styles: AnyStyleStructure,
    monitorKey?: string
) {
    if (!monitorKey || !isMigrationMonitored(monitorKey)) {
        return;
    }
    const droppedKeys = Object.entries(styles).flatMap(([styleKey, ruleset]) =>
        (ruleset && typeof ruleset === 'object'
            ? Object.keys(ruleset).filter((key) =>
                  deprecatedOnlyBreakpointKeys.has(key)
              )
            : []
        ).map((key) => `${styleKey}.${key}`)
    );
    if (droppedKeys.length) {
        logger.warn({
            message:
                'Deprecated breakpoints in already-migrated styles are dropped',
            widget: monitorKey,
            droppedKeys,
            tags: ['style-migration', 'style-migration-dropped-breakpoints'],
        });
    }
}

/**
 * A ruleset is already on the new structure as soon as ANY of its keys is a runtime breakpoint —
 * not only `common`. A producer is free to emit a single breakpoint (the monolith writes sizes at
 * `desktop` and leaves `common` out when nothing else is set), and treating that as no-breakpoints
 * input wraps the breakpoint map itself under `default` and renders it as garbage CSS.
 * No CSS property is named after a breakpoint, so this never mistakes flat styles for new ones.
 */
function hasRuntimeBreakpointKey(ruleset: unknown): boolean {
    return (
        !!ruleset &&
        typeof ruleset === 'object' &&
        Object.keys(ruleset).some((key) => runtimeBreakpointKeys.has(key))
    );
}

export function detectStyleStructure(
    styles: AnyStyleStructure
): StylesType | null {
    if (styles && typeof styles === 'object') {
        if ('rules' in styles) {
            return StylesType.OLD_BP;
        }
        const stylesValues = Object.values(styles);
        if (
            (stylesValues.length === 1 && (styles as any).buttonTypeCSSClass) || // button migration edge case
            stylesValues.length === 0 ||
            stylesValues.some(
                (ruleset) =>
                    hasRuntimeBreakpointKey(ruleset) ||
                    // checking for innerConfiguration breakpoints
                    Object.values(ruleset || {}).some(hasRuntimeBreakpointKey)
            )
        ) {
            return StylesType.NEW_BP;
        }
        return StylesType.NO_BP;
    }
    return null;
}
